// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { throttle, delay } from 'lodash/fp';
import { createStructuredSelector } from 'reselect';

// Utils
import liveCollaborationStoreConnect from '../store/liveCollaborationStoreConnect';
import { getRelativePositionFromDomEvent } from '../../../utils/dom/domEventUtil';
import { hasChanged } from '../../../utils/react/propsComparisons';

// Selectors
import { getGridSize } from '../../../utils/grid/gridSizeSelector';
import { updateCursor } from '../../../../common/users/liveCursor/liveCursorActions';
import { getCurrentUserId } from '../../../user/currentUserSelector';
import { getHasLiveCollaborators } from '../liveCollaborationSelector';
import { canvasZoomScaleSelector } from '../../../canvas/store/canvasZoomSelector';

const DEFAULT_TRANSLATION = { x: 0, y: 0 };

const CURSOR_STATUS = {
    SHOW: 'show',
    HIDE: 'hide',
};

const eventListenerNodeHasChanged = hasChanged('eventListenerNode');
const cursorTypeHasChanged = hasChanged('cursorType');

const mapStateToProps = createStructuredSelector({
    gridSize: getGridSize,
    zoomScale: canvasZoomScaleSelector,
    userId: getCurrentUserId,
});

const mapLiveCollaborationDispatchToProps = (dispatch) => ({
    dispatchCursorUpdate: ({ x, y, userId, status, cursorType }) =>
        dispatch(
            updateCursor({
                x,
                y,
                userId,
                status,
                cursorType,
            }),
        ),
});

@connect(mapStateToProps)
@liveCollaborationStoreConnect(null, mapLiveCollaborationDispatchToProps)
class LiveCursorTracker extends React.Component {
    cursorStatus = CURSOR_STATUS.SHOW;

    componentDidMount() {
        this.attachMouseMovementListeners(this.props);

        if (this.props.cursorType) {
            this.onCursorTypeChange();
        }
    }

    componentDidUpdate(prevProps) {
        if (eventListenerNodeHasChanged(prevProps, this.props)) {
            this.removeMouseMovementListeners(prevProps);
            this.attachMouseMovementListeners(this.props);
        }

        if (cursorTypeHasChanged(prevProps, this.props)) {
            this.onCursorTypeChange();
        }
    }

    componentWillUnmount() {
        this.removeMouseMovementListeners(this.props);
    }

    updateCursorStatus = (status) => {
        const { userId, cursorType, dispatchCursorUpdate } = this.props;

        this.cursorStatus = status;

        dispatchCursorUpdate({
            userId,
            cursorType,
            status,
        });
    };

    onMouseLeave = (event) => {
        if (this.showTimeoutId) clearTimeout(this.showTimeoutId);
        this.hideTimeoutId = delay(30, () => this.updateCursorStatus(CURSOR_STATUS.HIDE));
    };

    onMouseEnter = (event) => {
        if (this.hideTimeoutId) clearTimeout(this.hideTimeoutId);
        this.showTimeoutId = delay(160, () => this.updateCursorStatus(CURSOR_STATUS.SHOW));
    };

    onMouseMove = (event) => {
        const {
            gridSize,
            zoomScale,
            dispatchCursorUpdate,
            userId,
            eventListenerNode,
            translation = DEFAULT_TRANSLATION,
        } = this.props;

        const pos = getRelativePositionFromDomEvent(event, eventListenerNode);

        dispatchCursorUpdate({
            x: Math.round(pos.x + translation.x) / gridSize / zoomScale,
            y: Math.round(pos.y + translation.y) / gridSize / zoomScale,
            userId,
            status: this.cursorStatus,
        });
    };

    onCursorTypeChange = () => {
        const { userId, cursorType, dispatchCursorUpdate } = this.props;

        dispatchCursorUpdate({
            userId,
            cursorType,
        });
    };

    throttledMouseMove = throttle(120, this.onMouseMove);

    attachMouseMovementListeners(props) {
        const { eventListenerNode } = props;

        if (!eventListenerNode) return;

        eventListenerNode.addEventListener('pointermove', this.throttledMouseMove);
        eventListenerNode.addEventListener('dragover', this.throttledMouseMove);
        eventListenerNode.addEventListener('pointerleave', this.onMouseLeave);
        eventListenerNode.addEventListener('pointerenter', this.onMouseEnter);
        eventListenerNode.addEventListener('dragenter', this.onMouseEnter);
    }

    removeMouseMovementListeners(props) {
        const { eventListenerNode } = props;

        if (!eventListenerNode) return;

        eventListenerNode.removeEventListener('pointermove', this.throttledMouseMove);
        eventListenerNode.removeEventListener('dragover', this.throttledMouseMove);
        eventListenerNode.removeEventListener('pointerleave', this.onMouseLeave);
        eventListenerNode.removeEventListener('pointerenter', this.onMouseEnter);
        eventListenerNode.removeEventListener('dragenter', this.onMouseEnter);
    }

    render() {
        return null;
    }
}

const mapStateToPropsContainer = createStructuredSelector({
    hasLiveCollaborators: getHasLiveCollaborators,
});

const LiveCursorTrackerContainer = (props) => {
    const { hasLiveCollaborators } = props;

    if (!hasLiveCollaborators) return null;

    return <LiveCursorTracker {...props} />;
};

LiveCursorTrackerContainer.propTypes = LiveCursorTracker.propTypes = {
    gridSize: PropTypes.number,
    zoomScale: PropTypes.number,
    userId: PropTypes.string,
    cursorType: PropTypes.string,
    dispatchCursorUpdate: PropTypes.func,
    eventListenerNode: PropTypes.object,
    translation: PropTypes.object,
};

export default connect(mapStateToPropsContainer)(LiveCursorTrackerContainer);
